import {
  cloneElement,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { nanoid } from "nanoid";
import Head from "next/head";
import { createPortal } from "react-dom";
import { useContextSelector } from "use-context-selector";

import { ModalContainer } from "./styles";

import { ModalContext, ModalRootId } from "./contexts/ModalContext";
import AnimationDirection from "./interfaces/AnimationDirection";
import HorizontalAlign from "./interfaces/HorizontalAlign";
import VerticalAlign from "./interfaces/VerticalAlign";

interface FallbackPortalProps {
  disabled?: boolean;
  children: ReactElement;
}

function FallbackPortal({ disabled, children }: FallbackPortalProps): ReactElement {
  const key = useMemo(() => nanoid(), []);
  const container =
    typeof window !== "undefined" ? document.getElementById(ModalRootId) : null;

  if (!!container && !disabled) {
    return createPortal(children, container, key);
  }

  return children;
}

export interface ModalProps {
  show?: boolean;
  title?: string;
  horizontalAlign?: HorizontalAlign;
  verticalAlign?: VerticalAlign;
  animationFrom?: AnimationDirection;
  mobileResponsive?: boolean;
  onClose?: () => void;
  className?: string;
  autoDestroy?: boolean;
  destroyOnClose?: boolean;
  required?: boolean;
  createPortal?: boolean;
  children: ReactElement;
}

export function Modal({
  show = false,
  title,
  horizontalAlign = "center",
  verticalAlign = "center",
  mobileResponsive = true,
  animationFrom,
  onClose,
  className,
  autoDestroy = true,
  destroyOnClose = false,
  required = false,
  createPortal: portal = true,
  children,
}: ModalProps) {
  const triggerForceHide = useContextSelector(ModalContext, (v) => v.forceHide);
  const [forceHide, setForceHide] = useState(!show);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const preventCloseRef = useRef(false);
  const animateRef = useRef(!show);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    let hideTimeout: NodeJS.Timeout;
    let autoDestroyTimeout: NodeJS.Timeout;

    if (show) {
      setForceHide(false);
      preventCloseRef.current = false;
      animateRef.current = true;
    } else if (!forceHide) {
      hideTimeout = setTimeout(() => {
        if (destroyOnClose) {
          setForceHide(true);
        } else {
          containerRef.current.style.setProperty("display", "none");
        }
      }, 300);

      if (autoDestroy) {
        autoDestroyTimeout = setTimeout(() => {
          setForceHide(true);
        }, 60000);
      }
    } else if (forceHide) {
      containerRef.current.style.setProperty("display", "none");
    }

    return () => {
      clearTimeout(hideTimeout);
      clearTimeout(autoDestroyTimeout);
    };
  }, [show]);

  const visible = show && !forceHide;

  useEffect(() => {
    if (!visible) return undefined;

    // no pointer-events:none on body here: the fullscreen backdrop already blocks
    // the background, and disabling hit-testing mid-gesture (modal opens during an
    // active long-press touch) leaves the touch pipeline stuck on touch devices
    document.body.style.overflow = "hidden";
    containerRef.current?.style.removeProperty("display");

    return () => {
      const hasOpenModal = !!document.querySelector(`#${ModalRootId}>[data-show~=true]`);

      if (!hasOpenModal) {
        document.body.style.overflow = null;
      }
    };
  }, [visible]);

  function handleClose() {
    if (!preventCloseRef.current) onClose?.();
    preventCloseRef.current = true;
  }

  useEffect(() => {
    if (!triggerForceHide) return;

    setForceHide(true);

    if (show) handleClose();
  }, [triggerForceHide]);

  useEffect(() => {
    function touchHandler(event: TouchEvent) {
      if (event.cancelable && event.target === containerRef.current) {
        event.preventDefault();
        event.stopPropagation();

        handleClose();
      }
    }

    if (show && !required) {
      containerRef.current?.addEventListener("touchstart", touchHandler);
      containerRef.current?.addEventListener("mouseup", touchHandler);
    }

    return () => {
      containerRef.current?.removeEventListener("touchstart", touchHandler);
      containerRef.current?.removeEventListener("mouseup", touchHandler);
    };
  }, [show, required]);

  useEffect(() => {
    if (!visible) return undefined;

    // focus/ESC handling previously provided by focus-trap; don't steal focus
    // from children that autofocus themselves
    if (!containerRef.current?.contains(document.activeElement)) {
      containerRef.current?.focus({ preventScroll: true });
    }

    function keyHandler(event: KeyboardEvent) {
      if (event.key === "Escape" && !required) handleClose();
    }

    document.addEventListener("keydown", keyHandler);

    return () => document.removeEventListener("keydown", keyHandler);
  }, [visible, required]);

  return (
    <FallbackPortal disabled={!portal}>
      <>
        {show && title && (
          <Head>
            <title>{title}</title>
          </Head>
        )}
        <ModalContainer
          ref={containerRef}
          tabIndex={-1}
          data-show={show && !forceHide}
          data-animated={animateRef.current}
          className={className}
          horizontalAlign={horizontalAlign}
          verticalAlign={verticalAlign}
          animationFrom={animationFrom}
          mobileResponsive={mobileResponsive}
          role="dialog"
          aria-modal={true}
        >
          {!forceHide &&
            cloneElement(children, {
              showing: show,
              onClose: handleClose,
            })}
        </ModalContainer>
      </>
    </FallbackPortal>
  );
}

export interface UseModalProps {
  title?: string;
  defaultShow?: boolean;
  horizontalAlign?: HorizontalAlign;
  verticalAlign?: VerticalAlign;
  animationFrom?: AnimationDirection;
  mobileResponsive?: boolean;
  autoDestroy?: boolean;
  destroyOnClose?: boolean;
  required?: boolean;
  className?: string;
}

export function useModal(
  component: ReactElement,
  {
    title,
    defaultShow = false,
    horizontalAlign,
    verticalAlign,
    animationFrom,
    className,
    autoDestroy = true,
    destroyOnClose = false,
    mobileResponsive = true,
    required = false,
  }: UseModalProps
) {
  const addComponent = useContextSelector(ModalContext, (v) => v.addComponent);
  const removeComponent = useContextSelector(ModalContext, (v) => v.removeComponent);

  const [showing, setShowing] = useState(defaultShow);
  const [firstShow, setFirstShow] = useState(defaultShow);

  const handleClose = useCallback(() => {
    setShowing(false);
  }, []);

  useEffect(() => {
    if (showing) setFirstShow(true);
  }, [showing]);

  const modalComponent = useMemo(
    () => (
      <Modal
        show={showing}
        onClose={handleClose}
        title={title}
        horizontalAlign={horizontalAlign}
        verticalAlign={verticalAlign}
        animationFrom={animationFrom}
        className={className}
        autoDestroy={autoDestroy}
        destroyOnClose={destroyOnClose}
        mobileResponsive={mobileResponsive}
        required={required}
        createPortal={false}
      >
        {component}
      </Modal>
    ),
    [
      component,
      showing,
      horizontalAlign,
      verticalAlign,
      animationFrom,
      className,
      title,
      autoDestroy,
      destroyOnClose,
      mobileResponsive,
      required,
    ]
  );

  const key = useMemo(() => nanoid(), []);

  useEffect(() => {
    addComponent(key, modalComponent);
  }, [modalComponent]);

  useEffect(() => {
    return () => removeComponent(key);
  }, []);

  const show = useCallback(() => {
    setShowing(true);
  }, []);

  const hide = useCallback(() => setShowing(false), []);

  const toggle = useCallback((isShow?: boolean) => {
    const newShow = isShow != null ? isShow : !showing;

    setShowing(newShow);
  }, []);

  return {
    showing,
    firstShow,
    setShowing,
    show,
    hide,
    toggle,
  };
}
